/**
 * @fileoverview Reusable slide-over drawer panel (left or right) for the document builder shell.
 * @module ui-admin/components/SlideOverPanel
 *
 * Responsibilities: - Render an overlay drawer that slides in from the left or right edge of its
 *                     positioned ancestor, with a titled header, close (×) button, and scrollable body.
 *                   - Close on Escape and on backdrop click.
 * Dependencies:     - React, lucide-react (X).
 * Architecture:     - Presentation-only, host-neutral. Positioned `absolute` within the nearest
 *                     positioned ancestor (the builder canvas shell) so it overlays the canvas
 *                     gutter without pushing content. No portal — it is scoped to the shell so the
 *                     palette (left) and properties (right) drawers share one implementation (I-DRY).
 * Notes:            - No Dexie/API access, no mock data. Open/close state is owned by the caller.
 */

import React, { useCallback, useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * @contract SlideOverPanelProps
 * Purpose: Configure a single edge-anchored slide-over drawer.
 * Fields:
 * - side: Which edge the drawer anchors to and slides from ('left' | 'right').
 * - width: Drawer width in pixels (default 360).
 * - open: Whether the drawer is visible.
 * - onClose: Called when the user dismisses via ×, Escape, or backdrop click.
 * - title: Header label.
 * - children: Drawer body (scrolls internally).
 * - showBackdrop: Whether to dim and capture clicks behind the drawer (default true).
 * Rules: Caller owns open state; component performs no persistence or data access.
 */
export interface SlideOverPanelProps {
  side: 'left' | 'right';
  width?: number;
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  showBackdrop?: boolean;
}

/**
 * @component SlideOverPanel
 * Purpose:     Edge-anchored overlay drawer used for the palette (left) and properties (right).
 * Props:       SlideOverPanelProps.
 * Behavior:    Slides in/out via CSS transform; Escape and backdrop click call onClose.
 * Data Source: none (presentation only).
 * Constraints: no API/Dexie access; no mock data.
 */
export function SlideOverPanel({
  side, width = 360, open, onClose, title, children, showBackdrop = true,
}: SlideOverPanelProps) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  const hiddenTransform = side === 'left' ? 'translateX(-100%)' : 'translateX(100%)';

  return (
    <>
      {showBackdrop && open && (
        <div
          className="absolute inset-0 z-30 bg-black/20"
          data-testid={`slideover-backdrop-${side}`}
          aria-hidden="true"
          onClick={onClose}
        />
      )}
      <aside
        role="dialog"
        aria-label={title}
        aria-hidden={!open}
        data-testid={`slideover-${side}`}
        className={`absolute top-0 bottom-0 z-40 flex flex-col bg-white shadow-2xl transition-transform duration-200 ease-in-out ${side === 'left' ? 'left-0 border-r' : 'right-0 border-l'} border-gray-200`}
        style={{ width, transform: open ? 'translateX(0)' : hiddenTransform }}
      >
        <header className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
          <h2 className="flex-1 truncate text-sm font-semibold text-gray-800">{title}</h2>
          <button
            type="button"
            aria-label="Close panel"
            onClick={onClose}
            className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-orange-400"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">{open && children}</div>
      </aside>
    </>
  );
}

export default SlideOverPanel;
